import type { FamilyStats, PlayerStats } from '@cube-rush/shared';
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { backButton } from '../../app/backButton';
import { useApp } from '../../app/context';
import { ru } from '../../i18n/ru';
import { PAD_START_ATTR } from '../../input/focusNav';
import { cubeAccent } from '../../render/icons';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { CubePreview } from '../components/CubePreview';
import {
  bestScore,
  dateLabel,
  formatPlayTime,
  leftShare,
  orderPlayers,
  pickRivals,
  type Rivals,
  raceKind,
  recentRows,
} from '../stats/statsView';
import './StatsScreen.css';

/** A gamepad's focus starts at «В комнату». */
const padStart = { [PAD_START_ATTR]: '' };

/** Latest races shown before «Показать все». */
const RECENT_SHOWN = 5;

/**
 * Everybody's numbers, kid style: the two kids side by side (who is ahead), a card per player
 * (key numbers, favourite cube, streaks, records, latest races) and the whole family's totals.
 */
export function StatsScreen() {
  const app = useApp();
  // Android's Back and a gamepad's B return to the room, like closing a dialog (Back must not
  // leave the app from here).
  useLayoutEffect(() => backButton.register(() => app.closeStats()), [app]);
  const status = app.stats.status.value;
  const data = app.stats.data.value;
  const error = app.stats.error.value;
  const loading = status === 'loading';

  let body: ComponentChildren;
  if (data === null) {
    body =
      status === 'failed' && error !== null ? (
        <div class="stats__notice" role="alert">
          <p>{ru.stats.failed[error]}</p>
          <Button variant="yellow" onClick={() => void app.loadStats()}>
            {ru.stats.retry}
          </Button>
        </div>
      ) : (
        <p class="stats__notice" role="status">
          {ru.stats.loading}
        </p>
      );
  } else if (data.players.length === 0) {
    body = <p class="stats__notice">{ru.stats.empty}</p>;
  } else {
    const rivals = pickRivals(data);
    const players = orderPlayers(data.players, rivals ? [rivals.left.id, rivals.right.id] : []);
    body = (
      <>
        {rivals && <Versus rivals={rivals} />}
        <div class="stats__players">
          {players.map((player) => (
            <PlayerCard key={player.id} player={player} now={data.now} timeZone={data.timeZone} />
          ))}
        </div>
        <Family family={data.family} timeZone={data.timeZone} />
      </>
    );
  }

  return (
    <main class="screen stats">
      <header class="stats__head" {...padStart}>
        <Button
          variant="ghost"
          class="btn--square"
          icon="←"
          aria-label={ru.stats.back}
          onClick={() => app.closeStats()}
        />
        <h1 class="stats__title title-text">
          <span aria-hidden="true">📊</span> {ru.stats.title}
        </h1>
        <Button
          variant="ghost"
          class={`btn--square${loading ? ' stats__refresh--busy' : ''}`}
          icon="🔄"
          aria-label={ru.stats.refresh}
          disabled={loading}
          onClick={() => void app.loadStats()}
        />
      </header>
      <div class="stats__body" aria-busy={loading}>
        {status === 'failed' && error !== null && data !== null && (
          <p class="stats__stale" role="alert">
            {ru.stats.failed[error]}
          </p>
        )}
        {body}
      </div>
    </main>
  );
}

/** The two kids side by side: who is ahead in their races together, and their numbers. */
function Versus({ rivals }: { rivals: Rivals }) {
  const { left, right } = rivals;
  /** `text`: how a value reads to a screen reader; `shown`: on screen, in the narrow columns. */
  const rows: Array<{
    label: string;
    left: number;
    right: number;
    text?: (n: number) => string;
    shown?: (n: number) => string;
  }> = [
    { label: ru.stats.rows.wins, left: left.wins, right: right.wins },
    { label: ru.stats.rows.races, left: left.races, right: right.races },
    { label: ru.stats.rows.levels, left: left.levels, right: right.levels },
    { label: ru.stats.rows.deaths, left: left.deaths, right: right.deaths },
    {
      label: ru.stats.rows.time,
      left: left.playMs,
      right: right.playMs,
      text: (ms) => formatPlayTime(ms),
      shown: (ms) => formatPlayTime(ms, true),
    },
    {
      label: ru.stats.rows.bestWins,
      left: left.streaks.bestWins,
      right: right.streaks.bestWins,
    },
  ];
  return (
    <section class="versus panel" aria-label={ru.stats.versus}>
      <div class="versus__head">
        <Side player={left} />
        <div class="versus__score">
          <span class="versus__title">
            <span aria-hidden="true">⚔️</span> {ru.stats.versus}
          </span>
          {rivals.together > 0 ? (
            <>
              <strong class="versus__points" aria-hidden="true">
                {rivals.leftAhead}
                <span>:</span>
                {rivals.rightAhead}
              </strong>
              <span class="visually-hidden">
                {`${ru.stats.ahead}: ${left.profile.name} ${rivals.leftAhead}, ${right.profile.name} ${rivals.rightAhead}`}
              </span>
              <span class="versus__sub">{ru.stats.together(rivals.together)}</span>
            </>
          ) : (
            <span class="versus__sub">{ru.stats.together(0)}</span>
          )}
        </div>
        <Side player={right} />
      </div>
      <ul class="versus__rows">
        {rows.map((row) => {
          const text = row.text ?? String;
          const shown = row.shown ?? text;
          const share = leftShare(row.left, row.right);
          return (
            <li
              key={row.label}
              class="versus__row"
              aria-label={`${row.label}: ${left.profile.name} ${text(row.left)}, ${right.profile.name} ${text(row.right)}`}
            >
              <span class="versus__label" aria-hidden="true">
                {row.label}
              </span>
              <span class="versus__value" aria-hidden="true">
                {shown(row.left)}
              </span>
              <span class="versus__bar" aria-hidden="true">
                <span
                  class="versus__bar-left"
                  style={{
                    width: `${Math.round(share * 100)}%`,
                    background: cubeAccent(left.profile.skin),
                  }}
                />
                <span
                  class="versus__bar-right"
                  style={{ background: cubeAccent(right.profile.skin) }}
                />
              </span>
              <span class="versus__value versus__value--right" aria-hidden="true">
                {shown(row.right)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Side({ player }: { player: PlayerStats }) {
  return (
    <div class="versus__side">
      <Avatar avatar={player.profile.avatar} ring={cubeAccent(player.profile.skin)} />
      <span class="versus__who">
        <span class="versus__name">{player.profile.name}</span>
        {player.me && <span class="stats__me">{ru.stats.you}</span>}
      </span>
    </div>
  );
}

function PlayerCard({
  player,
  now,
  timeZone,
}: {
  player: PlayerStats;
  now: number;
  timeZone: string;
}) {
  const [showAll, setShowAll] = useState(false);
  const recent = recentRows(player.recent, now, timeZone);
  const shown = showAll ? recent : recent.slice(0, RECENT_SHOWN);
  const { streaks } = player;
  return (
    <article class="player-card panel" aria-label={player.profile.name}>
      <header class="player-card__head">
        <Avatar avatar={player.profile.avatar} ring={cubeAccent(player.profile.skin)} />
        <div class="player-card__who">
          <h2 class="player-card__name">{player.profile.name}</h2>
          {player.me && <span class="stats__me">{ru.stats.you}</span>}
        </div>
        {player.favourite && (
          <div class="player-card__fav">
            <CubePreview look={player.favourite.skin} size={30} glow label={ru.stats.favourite} />
            <span class="player-card__fav-label">
              {ru.stats.favourite}
              <br />
              {ru.stats.favouriteRaces(player.favourite.races)}
            </span>
          </div>
        )}
      </header>
      <div class="player-card__body">
        <div class="player-card__main">
          <ul class="tiles">
            <Tile icon="🏁" value={player.races} label={ru.stats.tiles.races} />
            <Tile icon="🏆" value={player.wins} label={ru.stats.tiles.wins} />
            <Tile icon="🥇" value={player.podiums} label={ru.stats.tiles.podiums} />
            <Tile icon="⭐" value={player.levels} label={ru.stats.tiles.levels} />
            <Tile icon="💀" value={player.deaths} label={ru.stats.tiles.deaths} />
            <Tile icon="⏱️" value={formatPlayTime(player.playMs)} label={ru.stats.tiles.time} />
          </ul>
          <p class="player-card__streaks">
            {streaks.days > 0 || streaks.wins > 0 ? (
              <>
                {streaks.days > 0 && (
                  <span class="streak">
                    <span aria-hidden="true">🔥</span> {ru.stats.daysInRow(streaks.days)}
                    {streaks.bestDays > streaks.days && (
                      <span class="streak__record"> ({ru.stats.record(streaks.bestDays)})</span>
                    )}
                  </span>
                )}
                {streaks.wins > 0 && (
                  <span class="streak">
                    <span aria-hidden="true">🏆</span> {ru.stats.winsInRow(streaks.wins)}
                    {streaks.bestWins > streaks.wins && (
                      <span class="streak__record"> ({ru.stats.record(streaks.bestWins)})</span>
                    )}
                  </span>
                )}
              </>
            ) : (
              <span class="streak streak--none">{ru.stats.noStreak}</span>
            )}
          </p>
          <section class="player-card__part">
            <h3 class="player-card__part-title">
              <span aria-hidden="true">🏅</span> {ru.stats.bests}
            </h3>
            {player.bests.length > 0 ? (
              <ul class="bests">
                {player.bests.map((best) => (
                  <li key={`${best.format}-${best.difficulty}-${best.length}`} class="bests__row">
                    <span class="bests__kind">{raceKind(best)}</span>
                    <strong class="bests__score">{bestScore(best)}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p class="player-card__empty">{ru.stats.noBests}</p>
            )}
          </section>
        </div>
        {recent.length > 0 && (
          <section class="player-card__part">
            <h3 class="player-card__part-title">
              <span aria-hidden="true">🕹️</span> {ru.stats.recent}
            </h3>
            <ol class="recent">
              {shown.map((row) => (
                <li key={row.key} class="recent__row">
                  <span class="recent__place" aria-hidden={row.place === '—' ? 'true' : undefined}>
                    {row.place}
                  </span>
                  <span class="recent__what">
                    <span class="recent__kind">{row.kind}</span>
                    <span class="recent__when">
                      {row.day} · {row.company}
                    </span>
                  </span>
                  <span class="recent__numbers">
                    <span class="recent__time">{row.time}</span>
                    <span class="recent__levels">
                      <span aria-hidden="true">⭐</span> {row.levels}{' '}
                      <span aria-hidden="true">💀</span> {row.deaths}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
            {recent.length > RECENT_SHOWN && (
              <button type="button" class="player-card__more" onClick={() => setShowAll(!showAll)}>
                {showAll ? ru.stats.showLess : ru.stats.showAll(recent.length)}
              </button>
            )}
          </section>
        )}
      </div>
    </article>
  );
}

function Tile({ icon, value, label }: { icon: string; value: number | string; label: string }) {
  return (
    <li class="tile">
      <span class="tile__icon" aria-hidden="true">
        {icon}
      </span>
      <strong class={`tile__value${typeof value === 'string' ? ' tile__value--text' : ''}`}>
        {value}
      </strong>
      <span class="tile__label">{label}</span>
    </li>
  );
}

function Family({ family, timeZone }: { family: FamilyStats; timeZone: string }) {
  return (
    <section class="family panel" aria-label={ru.stats.family}>
      <h2 class="family__title">
        <span aria-hidden="true">🏠</span> {ru.stats.family}
      </h2>
      <ul class="tiles tiles--family">
        <Tile icon="🏁" value={family.races} label={ru.stats.familyTiles.races} />
        <Tile icon="⏱️" value={formatPlayTime(family.playMs)} label={ru.stats.familyTiles.time} />
        <Tile icon="⭐" value={family.levels} label={ru.stats.familyTiles.levels} />
        <Tile icon="💀" value={family.deaths} label={ru.stats.familyTiles.deaths} />
        <Tile icon="📅" value={family.days} label={ru.stats.familyTiles.days} />
      </ul>
      {family.since !== null && (
        <p class="family__since">{ru.stats.since(dateLabel(family.since, timeZone))}</p>
      )}
    </section>
  );
}
